import { distinctUntilChanged, filter, map, type Observable } from 'rxjs';
import { isTruthy } from '@ardoq/common-helpers';
import {
  location$,
  type AppLocation,
  type AppRouterLocation,
} from 'router/declarative';
import { isRouteActive } from 'router/declarative/filterRouteActive';
import { RootPages } from '../types';

export type IntegrationRoute = {
  readonly id: string | null;
  readonly path: string | null;
};

/**
 * Extract integration path from the route.
 * Route like '/integrations/:integrationId/import/configure'
 * becomes path: 'import/configure'
 */
const extractIntegrationPath = (route: string): string | null => {
  const match = route.match(/^\/integrations\/:integrationId\/(.+)$/);
  return match?.[1] ?? null;
};

const toIntegrationRoute = (
  location: AppLocation<'/integrations'>
): IntegrationRoute => {
  if (location.route === '/integrations/history') {
    return { id: null, path: RootPages.HISTORY };
  }
  if (location.route === '/integrations/dashboard') {
    return { id: null, path: RootPages.NEW_DASHBOARD };
  }
  return {
    id:
      'integrationId' in location.params ? location.params.integrationId : null,
    path: extractIntegrationPath(location.route),
  };
};

const toIntegrationRouteOrNull = (
  location: AppRouterLocation
): IntegrationRoute | null => {
  if (!isRouteActive(location, '/integrations')) {
    return null;
  }
  return toIntegrationRoute(location);
};

const areRoutesEqual = (
  prev: IntegrationRoute | null,
  curr: IntegrationRoute | null
): boolean => {
  if (prev === null || curr === null) {
    return prev === curr;
  }
  return prev.id === curr.id && prev.path === curr.path;
};

export const integrationRoute$: Observable<IntegrationRoute> = location$.pipe(
  map(toIntegrationRouteOrNull),
  distinctUntilChanged(areRoutesEqual),
  filter(isTruthy)
);
